import VARIANT from './ButtonType/constant/variant';
import { colors, radius, elevationCss, spacings } from '../Token/token';
import { appendPx } from '../Token/helper/tokenHelper';

/**
 * Button variations
 * @default primary
 */
export type Variant =
  | 'primary'
  | 'primary-green'
  | 'secondary-main'
  | 'main-cta'
  | 'secondary'
  | 'text'
  | 'text-underlined'
  | 'destructive-primary'
  | 'circular-primary'
  | 'circular-primary-green'
  | 'circular-main-cta'
  | 'circular-outline-black'
  | 'circular-outline-primary'
  | 'circular-outline-white'
  | 'outline-primary'
  | 'outline-primary-green'
  | 'outline-black'
  | 'outline-white'
  | 'outline-radio-inactive';

export function getButtonStyle(
  variant: string,
  elevation: string
): {
  [key: string]: any;
} {
  switch (variant) {
    case VARIANT.PRIMARY:
      return {
        backgroundColor: colors.uiOrangePrimary,
        color: colors.uiLightPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.PRIMARY_GREEN:
      return {
        backgroundColor: colors.uiGreenTeal,
        color: colors.uiLightPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.PRIMARY_DUSK_GREEN:
      return {
        backgroundColor: colors.uiDirtyGreen,
        color: colors.uiLightPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.SECONDARY_MAIN:
      return {
        backgroundColor: colors.uiBlueLightPrimary,
        color: colors.uiLightPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.MAIN_CTA:
      return {
        backgroundColor: colors.uiBluePrimary,
        color: colors.uiLightPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.SECONDARY:
      return {
        backgroundColor: colors.uiLightSecondary,
        color: colors.uiBluePrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.TEXT:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiOrangePrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.TEXT_UNDERLINED:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiOrangePrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.TEXT_BLACK:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiDarkPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.TEXT_WHITE:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiLightPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.TEXT_DESTRUCTIVE:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiRedPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.DESTRUCTIVE_PRIMARY:
      return {
        backgroundColor: colors.uiRedPrimary,
        color: colors.uiLightPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.DESTRUCTIVE_SECONDARY:
      return {
        backgroundColor: colors.uiLightSecondary,
        color: colors.uiRedPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.OUTLINE_BLACK:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiDarkPrimary,
        border: `1px solid ${colors.uiDarkPrimary}`,
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.OUTLINE_PRIMARY:
      return {
        backgroundColor: colors.uiLightPrimary,
        color: colors.uiOrangePrimary,
        border: `1px solid ${colors.uiOrangePrimary}`,
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.OUTLINE_PRIMARY_GREEN:
      return {
        backgroundColor: colors.uiLightPrimary,
        color: colors.uiGreenTeal,
        border: `1px solid ${colors.uiGreenTeal}`,
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.OUTLINE_WHITE:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiLightPrimary,
        border: `1px solid ${colors.uiLightPrimary}`,
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.CIRCULAR_PRIMARY:
      return {
        backgroundColor: colors.uiOrangePrimary,
        color: colors.uiLightPrimary,
        borderRadius: appendPx(radius.rounded),
        boxShadow: elevationCss[elevation],
        border: 'none',
        paddingRight: appendPx(spacings.xxs),
        paddingLeft: appendPx(spacings.xs)
      };
    case VARIANT.CIRCULAR_PRIMARY_GREEN:
      return {
        backgroundColor: colors.uiGreenTeal,
        color: colors.uiLightPrimary,
        borderRadius: appendPx(radius.rounded),
        boxShadow: elevationCss[elevation],
        border: 'none',
        paddingRight: appendPx(spacings.xxs),
        paddingLeft: appendPx(spacings.xs)
      };
    case VARIANT.CIRCULAR_MAIN_CTA:
      return {
        backgroundColor: colors.uiBluePrimary,
        color: colors.uiLightPrimary,
        borderRadius: appendPx(radius.rounded),
        boxShadow: elevationCss[elevation],
        border: 'none',
        paddingRight: appendPx(spacings.md),
        paddingLeft: appendPx(spacings.md)
      };
    case VARIANT.CIRCULAR_OUTLINE_PRIMARY:
      return {
        backgroundColor: colors.uiLightPrimary,
        color: colors.uiOrangePrimary,
        borderRadius: appendPx(radius.rounded),
        boxShadow: elevationCss[elevation],
        border: `1px solid ${colors.uiOrangePrimary}`,
        paddingRight: appendPx(spacings.md),
        paddingLeft: appendPx(spacings.xs)
      };
    case VARIANT.CIRCULAR_OUTLINE_BLACK:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiDarkPrimary,
        borderRadius: appendPx(radius.rounded),
        boxShadow: elevationCss[elevation],
        border: `1px solid ${colors.uiDarkPrimary}`,
        paddingRight: appendPx(spacings.md),
        paddingLeft: appendPx(spacings.xs)
      };
    case VARIANT.CIRCULAR_OUTLINE_PRIMARY:
      return {
        backgroundColor: colors.uiLightPrimary,
        color: colors.uiOrangePrimary,
        borderRadius: appendPx(radius.rounded),
        boxShadow: elevationCss[elevation],
        border: `1px solid ${colors.uiOrangePrimary}`,
        paddingRight: appendPx(spacings.xxs),
        paddingLeft: appendPx(spacings.xxs)
      };
    case VARIANT.CIRCULAR_OUTLINE_PRIMARY:
      return {
        backgroundColor: colors.uiLightPrimary,
        color: colors.uiOrangePrimary,
        borderRadius: appendPx(radius.rounded),
        boxShadow: elevationCss[elevation],
        border: `1px solid ${colors.uiOrangePrimary}`,
        paddingRight: appendPx(spacings.xxs),
        paddingLeft: '0px'
      };
    case VARIANT.CIRCULAR_OUTLINE_WHITE:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiLightPrimary,
        borderRadius: appendPx(radius.rounded),
        boxShadow: elevationCss[elevation],
        border: `1px solid ${colors.uiLightPrimary}`,
        paddingRight: appendPx(spacings.md),
        paddingLeft: appendPx(spacings.md)
      };
    case VARIANT.OUTLINE_RADIO_INACTIVE:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiBlueLight,
        border: `1px solid ${colors.uiGrayLighter}`,
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.RADIO_PRIMARY_GREEN:
      return {
        backgroundColor: colors.uiLimeGreenLight,
        color: colors.uiLimeGreen,
        border: `1px solid ${colors.uiLimeGreen}`,
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    case VARIANT.RADIO_OUTLINE_NOT_SELECTED:
      return {
        backgroundColor: colors.transparent,
        color: colors.uiBlueDarker,
        border: `1px solid ${colors.uiGrayLighter}`,
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
    default:
      return {
        backgroundColor: colors.uiOrangePrimary,
        color: colors.uiLightPrimary,
        border: 'none',
        borderRadius: appendPx(radius.default),
        boxShadow: elevationCss[elevation]
      };
  }
}
